import React, { useState, useEffect, useRef, useCallback } from 'react';
import FrameworkExplorer from './frameworkvisualizer';
import Papa from 'papaparse';
import HealthDataVisualizer from './HealthDataVisualizer';
import HealthDataVisualizer2 from './HealthDataVisualizer_2';
import Assessment from './Assessment';
import OECDExplorer from './OECDExplorer';
import Plotly from 'plotly.js-dist';

// Animated World Map Mini-Visualization using Plotly Choropleth - CDIC Countries Only
const AnimatedWorldMap = () => {
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const mapRef = useRef(null);
  const intervalRef = useRef(null);

  const years = [2000, 2005, 2010, 2015, 2020, 2023];

  // CDIC Countries with life expectancy data
  const cdicData = [
    { iso: 'BGD', name: 'Bangladesh', values: [62.0, 65.5, 68.0, 70.5, 71.4, 74.7] },
    { iso: 'BRA', name: 'Brazil', values: [70.3, 71.7, 73.4, 75.2, 75.9, 76.4] },
    { iso: 'KHM', name: 'Cambodia', values: [58.4, 62.7, 67.1, 69.8, 70.5, 71.2] },
    { iso: 'CMR', name: 'Cameroon', values: [53.3, 54.9, 57.0, 59.7, 61.7, 63.7] },
    { iso: 'COL', name: 'Colombia', values: [71.0, 73.0, 74.0, 76.1, 77.3, 77.9] },
    { iso: 'CIV', name: "Cote d'Ivoire", values: [50.2, 51.8, 54.0, 57.4, 59.6, 61.5] },
    { iso: 'ECU', name: 'Ecuador', values: [73.1, 74.5, 75.6, 76.5, 77.0, 77.6] },
    { iso: 'ETH', name: 'Ethiopia', values: [52.2, 57.0, 62.2, 66.0, 67.8, 68.7] },
    { iso: 'GIN', name: 'Guinea', values: [52.7, 55.2, 57.7, 60.3, 62.1, 63.5] },
    { iso: 'IND', name: 'India', values: [62.7, 64.9, 67.2, 69.3, 70.2, 72.0] },
    { iso: 'IDN', name: 'Indonesia', values: [66.3, 67.9, 69.4, 70.8, 71.5, 72.3] },
    { iso: 'KEN', name: 'Kenya', values: [56.1, 58.5, 60.9, 62.3, 61.6, 63.6] },
    { iso: 'MYS', name: 'Malaysia', values: [72.7, 74.4, 75.4, 75.3, 76.1, 76.7] },
    { iso: 'MAR', name: 'Morocco', values: [68.7, 70.7, 73.3, 75.5, 76.7, 77.4] },
    { iso: 'NER', name: 'Niger', values: [50.8, 54.5, 58.4, 61.0, 62.4, 63.6] },
    { iso: 'PER', name: 'Peru', values: [71.6, 73.1, 74.5, 76.0, 76.5, 77.2] },
    { iso: 'SEN', name: 'Senegal', values: [58.8, 62.3, 65.1, 67.5, 68.8, 69.8] },
    { iso: 'THA', name: 'Thailand', values: [70.6, 73.3, 74.4, 75.5, 78.0, 78.7] },
    { iso: 'TUN', name: 'Tunisia', values: [73.0, 74.3, 75.3, 76.0, 76.7, 77.3] },
    { iso: 'VNM', name: 'Viet Nam', values: [73.0, 74.2, 75.1, 75.8, 75.4, 76.0] },
  ];

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentYearIndex(prev => (prev + 1) % years.length);
      }, 1500);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, years.length]);

  useEffect(() => {
    if (!mapRef.current) return;

    const locations = cdicData.map(c => c.iso);
    const values = cdicData.map(c => c.values[currentYearIndex]);
    const texts = cdicData.map(c => `${c.name}: ${c.values[currentYearIndex].toFixed(1)} years`);

    const minVal = 45;
    const maxVal = 85;
    const normalizedValues = values.map(v => ((v - minVal) / (maxVal - minVal)) * 100);

    const data = [{
      type: 'choropleth',
      locations: locations,
      z: normalizedValues,
      text: texts,
      colorscale: [
        [0, '#ff0000'],
        [0.25, '#ffa700'],
        [0.5, '#fff400'],
        [0.75, '#a3ff00'],
        [1, '#2cba00']
      ],
      zmin: 0,
      zmax: 100,
      marker: {
        line: {
          color: '#ffffff',
          width: 0.5
        }
      },
      showscale: false,
      hoverinfo: 'text'
    }];

    const layout = {
      geo: {
        scope: 'world',
        projection: { 
          type: 'natural earth',
          scale: 1.15
        },
        center: { lon: 20, lat: 10 },
        showframe: false,
        showcoastlines: true,
        coastlinecolor: '#D6D6DA',
        showland: true,
        landcolor: '#E5E7EB',
        showocean: true,
        oceancolor: '#1e3a5f',
        showlakes: false,
        showcountries: true,
        countrycolor: '#D6D6DA',
        countrywidth: 0.5,
        bgcolor: '#1e3a5f'
      },
      margin: { l: 0, r: 0, t: 28, b: 0 },
      paper_bgcolor: '#1e3a5f',
      plot_bgcolor: '#1e3a5f',
      dragmode: false,
      title: {
        text: 'Life Expectancy at Birth (Years)',
        font: { size: 12, color: '#94a3b8', family: 'Arial' },
        x: 0.5,
        xanchor: 'center',
        y: 0.98,
        yanchor: 'top'
      },
      annotations: [{
        x: 0.98,
        y: 0.95,
        xref: 'paper',
        yref: 'paper',
        text: `<b>${years[currentYearIndex]}</b>`,
        showarrow: false,
        font: { size: 16, color: '#3B82F6', family: 'Arial' },
        xanchor: 'right',
        yanchor: 'top'
      }]
    };

    const config = {
      displayModeBar: false,
      responsive: true,
      staticPlot: true
    };

    if (Plotly) {
      Plotly.react(mapRef.current, data, layout, config);
    }
  }, [currentYearIndex, years]);

  return (
    <div 
      style={{
        width: '100%',
        height: '260px',
        backgroundColor: '#1e3a5f',
        borderRadius: '8px',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #E5E7EB'
      }}
      onMouseEnter={() => setIsPlaying(false)}
      onMouseLeave={() => setIsPlaying(true)}
    >
      <div 
        ref={mapRef}
        style={{ width: '100%', height: '100%' }}
      />
      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: 'rgba(255,255,255,0.9)',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '10px',
        color: '#6B7280'
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isPlaying ? '#10B981' : '#F59E0B'
        }} />
        {isPlaying ? 'Playing' : 'Paused'}
      </div>
    </div>
  );
};

// Animated Line Chart — Fiscal Space (Macroeconomic Growth) for CDiC Countries
// Data sourced from Fiscal_Space_Feb2.csv — "Potential Fiscal Space Created for Macroeconomic growth"
// Unit: Total Additional per Capita Fiscal Space for Health (Current USD)
const AnimatedLineChart = () => {
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const chartRef = useRef(null);
  const intervalRef = useRef(null);

  const years = [2025, 2026, 2027, 2028, 2029, 2030];

  // 8 CDiC countries selected for visual diversity (from Fiscal_Space_Feb2.csv, Macroeconomic growth variable)
  const countries = [
    { name: 'Colombia',  iso: 'COL', values: [92.35, 115.35, 133.85, 154.27, 176.32, 198.22], color: '#3B82F6' },
    { name: 'Brazil',    iso: 'BRA', values: [58.84, 65.51,  88.37, 111.52, 136.94, 163.88], color: '#10B981' },
    { name: 'Malaysia',  iso: 'MYS', values: [34.46, 52.97,  69.70,  87.57, 105.71, 124.21], color: '#8B5CF6' },
    { name: 'Thailand',  iso: 'THA', values: [34.20, 35.83,  46.27,  58.52,  72.09,  86.61], color: '#F59E0B' },
    { name: 'Ecuador',   iso: 'ECU', values: [36.20, 46.32,  59.57,  73.38,  88.21, 104.28], color: '#EC4899' },
    { name: 'Morocco',   iso: 'MAR', values: [29.25, 38.90,  46.12,  53.65,  61.46,  70.21], color: '#06B6D4' },
    { name: 'India',     iso: 'IND', values: [ 6.88, 10.15,  14.00,  18.22,  22.81,  27.82], color: '#EF4444' },
    { name: 'Ethiopia',  iso: 'ETH', values: [-0.50,  0.35,   1.25,   2.28,   3.43,   4.67], color: '#F97316' },
  ];

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentYearIndex(prev => {
          if (prev >= years.length - 1) {
            return 0;
          }
          return prev + 1;
        });
      }, 1200);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, years.length]);

  useEffect(() => {
    if (!chartRef.current) return;

    const visibleCount = currentYearIndex + 1;
    const visibleYears = years.slice(0, visibleCount);

    const traces = countries.map((country) => {
      const visibleValues = country.values.slice(0, visibleCount);

      return {
        x: visibleYears,
        y: visibleValues,
        type: 'scatter',
        mode: 'lines+markers',
        name: country.name,
        line: {
          color: country.color,
          width: 2.5,
          shape: 'spline'
        },
        marker: {
          color: country.color,
          size: visibleValues.map((_, i) => i === visibleValues.length - 1 ? 8 : 4),
          line: {
            color: 'white',
            width: visibleValues.map((_, i) => i === visibleValues.length - 1 ? 2 : 0)
          }
        },
        hovertemplate: `<b>${country.name}</b><br>Year: %{x}<br>$%{y:.2f} per capita<extra></extra>`
      };
    });

    const layout = {
      title: {
        text: 'Fiscal Space — Macroeconomic Growth (per Capita USD)',
        font: { size: 11, color: '#374151', family: 'Arial' },
        x: 0.5,
        xanchor: 'center',
        y: 0.98
      },
      xaxis: {
        tickmode: 'array',
        tickvals: years,
        ticktext: years.map(String),
        tickfont: { size: 9, color: '#6B7280' },
        showgrid: false,
        zeroline: false,
        range: [2024.5, 2030.5],
        title: {
          text: 'Year',
          font: { size: 10, color: '#6B7280' },
          standoff: 10
        }
      },
      yaxis: {
        title: {
          text: 'USD per Capita',
          font: { size: 10, color: '#6B7280' }
        },
        tickfont: { size: 9, color: '#6B7280' },
        showgrid: true,
        gridcolor: '#E5E7EB',
        zeroline: true,
        zerolinecolor: '#D1D5DB',
        range: [-10, 220]
      },
      showlegend: true,
      legend: {
        font: { size: 7.5, color: '#374151' },
        orientation: 'h',
        x: 0.5,
        xanchor: 'center',
        y: -0.35,
        yanchor: 'top',
        tracegroupgap: 2,
        itemwidth: 20
      },
      margin: { l: 50, r: 15, t: 30, b: 65 },
      paper_bgcolor: '#FAFBFC',
      plot_bgcolor: '#FAFBFC',
      hovermode: 'closest',
      annotations: [{
        x: 0.98,
        y: 0.92,
        xref: 'paper',
        yref: 'paper',
        text: `<b>${years[currentYearIndex]}</b>`,
        showarrow: false,
        font: { size: 16, color: '#3B82F6', family: 'Arial' },
        xanchor: 'right',
        yanchor: 'top'
      }]
    };

    const config = {
      displayModeBar: false,
      responsive: true,
      staticPlot: false
    };

    Plotly.react(chartRef.current, traces, layout, config);
  }, [currentYearIndex, years]);

  return (
    <div 
      style={{
        width: '100%',
        height: '260px',
        backgroundColor: '#FAFBFC',
        borderRadius: '8px',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #E5E7EB'
      }}
      onMouseEnter={() => setIsPlaying(false)}
      onMouseLeave={() => setIsPlaying(true)}
    >
      <div 
        ref={chartRef}
        style={{ width: '100%', height: '100%' }}
      />
      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: 'rgba(255,255,255,0.9)',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '10px',
        color: '#6B7280'
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isPlaying ? '#10B981' : '#F59E0B',
          animation: isPlaying ? 'pulse 1.5s infinite' : 'none'
        }} />
        {isPlaying ? 'Playing' : 'Paused'}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

const LandingPage = ({ 
  onNavigateToVisualizer, 
  previewData,
  onSearch 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [variableList, setVariableList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [metadata, setMetadata] = useState([]);
  const [showMetadata, setShowMetadata] = useState(false);
  const [selectedVariableMetadata, setSelectedVariableMetadata] = useState(null);
  const [initialVariableSelection, setInitialVariableSelection] = useState(null);
  
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const policyDropdownRef = useRef(null);
  
  const [showFramework, setShowFramework] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [showIncidence, setShowIncidence] = useState(false);
  const [showFiscalSpace, setShowFiscalSpace] = useState(false);
  const [showOECDExplorer, setShowOECDExplorer] = useState(false);
  const [showUploadPortal, setShowUploadPortal] = useState(false);
  const [showPolicyDropdown, setShowPolicyDropdown] = useState(false);
  const [card1Hovered, setCard1Hovered] = useState(false);
  const [card2Hovered, setCard2Hovered] = useState(false);

  const navigateTo = useCallback((view, pushHistory = true) => {
    setShowFramework(false);
    setShowAssessment(false);
    setShowIncidence(false);
    setShowFiscalSpace(false);
    setShowOECDExplorer(false);
    setShowUploadPortal(false);
    setInitialVariableSelection(null);
    
    if (view === 'home') {
      setSearchQuery('');
      setShowAutocomplete(false);
    }
    
    switch(view) {
      case 'home':
        break;
      case 'observatory':
        setShowOECDExplorer(true);
        break;
      case 'assessment':
        setShowAssessment(true);
        break;
      case 'incidence':
        setShowIncidence(true);
        break;
      case 'fiscal-space':
        setShowFiscalSpace(true);
        break;
      case 'framework':
        setShowFramework(true);
        break;
      case 'upload-portal':
        setShowUploadPortal(true);
        break;
      default:
        break;
    }
    
    if (pushHistory) {
      const url = view === 'home' ? window.location.pathname : `${window.location.pathname}#${view}`;
      window.history.pushState({ view }, '', url);
    }
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      const view = event.state?.view || 'home';
      navigateTo(view, false);
    };

    window.addEventListener('popstate', handlePopState);
    
    const hash = window.location.hash.slice(1);
    if (hash) {
      navigateTo(hash, false);
    } else {
      window.history.replaceState({ view: 'home' }, '', window.location.pathname);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigateTo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (policyDropdownRef.current && !policyDropdownRef.current.contains(event.target)) {
        setShowPolicyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const loadLists = async () => {
      try {
        const datasetResponse = await fetch('/Current/Dataset.csv');
        if (datasetResponse.ok) {
          const datasetText = await datasetResponse.text();
          Papa.parse(datasetText, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const uniqueVariables = [...new Set(
                results.data
                  .map(row => row.Variable?.trim())
                  .filter(v => v && v.length > 0)
              )].sort();
              setVariableList(uniqueVariables);
            }
          });
        }
        
        const countryResponse = await fetch('/Current/country_list.txt');
        if (countryResponse.ok) {
          const countryText = await countryResponse.text();
          const countries = countryText.split('\n')
            .map(c => c.trim())
            .filter(c => c && c.length > 0);
          setCountryList(countries);
        }
        
        const metadataResponse = await fetch('/Current/MetaData.csv');
        if (metadataResponse.ok) {
          const metadataText = await metadataResponse.text();
          Papa.parse(metadataText, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const cleanedMetadata = results.data.filter(row => 
                row && row.Variable && typeof row.Variable === 'string' && row.Variable.trim()
              );
              setMetadata(cleanedMetadata);
            }
          });
        }
      } catch (error) {
        console.error('Error loading lists:', error);
      }
    };
    
    loadLists();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setAutocompleteOptions([]);
      setShowAutocomplete(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const maxResults = 10;
    
    const matchedVariables = variableList
      .filter(v => v.toLowerCase().includes(query))
      .slice(0, maxResults)
      .map(v => ({ type: 'variable', value: v }));
    
    setAutocompleteOptions(matchedVariables);
    setShowAutocomplete(matchedVariables.length > 0);
    setSelectedIndex(-1);
  }, [searchQuery, variableList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current && 
        !autocompleteRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectOption = (option) => {
    setSearchQuery(option.value);
    setShowAutocomplete(false);
    
    const variableMetadata = metadata.find(m => 
      m.Variable && m.Variable.trim() === option.value.trim()
    );
    
    if (variableMetadata) {
      const component = (variableMetadata['System Components'] || variableMetadata['Component'] || '').trim();
      const subComponent = (variableMetadata['Sub-Components'] || variableMetadata['Sub-component'] || '').trim();
      const measure = (variableMetadata['Unit of Measure'] || variableMetadata['Measure'] || '').trim();
      
      setInitialVariableSelection({
        variable: option.value.trim(),
        component: component,
        subComponent: subComponent,
        measure: measure
      });
    } else {
      setInitialVariableSelection({
        variable: option.value.trim(),
        component: '',
        subComponent: '',
        measure: ''
      });
    }
    
    setShowOECDExplorer(true);
    setShowFramework(false);
    setShowAssessment(false);
    setShowIncidence(false);
    setShowFiscalSpace(false);
    setShowUploadPortal(false);
    window.history.pushState({ view: 'observatory' }, '', `${window.location.pathname}#observatory`);
  };

  const handleKeyDown = (e) => {
    if (!showAutocomplete) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < autocompleteOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < autocompleteOptions.length) {
        handleSelectOption(autocompleteOptions[selectedIndex]);
      } else {
        handleSearchSubmit(e);
      }
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
      setSelectedIndex(-1);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const matchingVariable = variableList.find(v => 
        v.toLowerCase() === searchQuery.trim().toLowerCase()
      );
      
      if (matchingVariable) {
        handleSelectOption({ type: 'variable', value: matchingVariable });
      } else {
        if (onSearch) {
          onSearch(searchQuery);
        }
        setInitialVariableSelection(null);
        setShowOECDExplorer(true);
        setShowFramework(false);
        setShowAssessment(false);
        setShowIncidence(false);
        setShowFiscalSpace(false);
        setShowUploadPortal(false);
        window.history.pushState({ view: 'observatory' }, '', `${window.location.pathname}#observatory`);
      }
    }
  };

  const isPolicyAnalyzerActive = showAssessment || showIncidence || showFiscalSpace;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* ─── NAVBAR ─── */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '2px solid #e5e7eb',
        padding: '0.5rem 2rem',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('home');
              }}
              style={{ display: 'block' }}
            >
              <img
                src="/logo.png"
                alt="Harvard Health Systems Innovation Logo"
                style={{ height: '45px', width: 'auto', display: 'block' }}
              />
            </a>
          </div>
          
          <nav style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
            {/* Health Systems Visualizer */}
            <a 
              href="#observatory" 
              onClick={(e) => {
                e.preventDefault();
                navigateTo('observatory');
                setShowPolicyDropdown(false);
              }}
              style={{ 
                color: showOECDExplorer ? '#3B82F6' : '#1F2937',
                textDecoration: 'none', 
                fontSize: '0.95rem', 
                fontWeight: '500',
                padding: '0.5rem 0',
                borderBottom: showOECDExplorer ? '2px solid #3B82F6' : '2px solid transparent'
              }}
            >
              Health Systems Visualizer
            </a>

            {/* GC-CDiC Studies dropdown */}
            <div 
              ref={policyDropdownRef}
              style={{ position: 'relative' }}
              onMouseEnter={() => setShowPolicyDropdown(true)}
              onMouseLeave={() => setShowPolicyDropdown(false)}
            >
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowPolicyDropdown(!showPolicyDropdown);
                }}
                style={{ 
                  color: isPolicyAnalyzerActive ? '#3B82F6' : '#1F2937',
                  textDecoration: 'none', 
                  fontSize: '0.95rem', 
                  fontWeight: '500',
                  padding: '0.5rem 0',
                  borderBottom: isPolicyAnalyzerActive ? '2px solid #3B82F6' : '2px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                GC-CDiC Studies
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="none" 
                  style={{
                    transform: showPolicyDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                >
                  <path 
                    d="M2.5 4.5L6 8L9.5 4.5" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </a>

              {showPolicyDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  paddingTop: '0.5rem',
                  zIndex: 1000
                }}>
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    border: '1px solid #E5E7EB',
                    minWidth: '280px',
                    overflow: 'hidden'
                  }}>
                    {/* T1D Readiness Assessment - NEW Assessment.js */}
                    <a
                      href="#assessment"
                      onClick={(e) => {
                        e.preventDefault();
                        navigateTo('assessment');
                        setShowPolicyDropdown(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '1rem 1.25rem',
                        textDecoration: 'none',
                        color: showAssessment ? '#3B82F6' : '#1F2937',
                        backgroundColor: showAssessment ? '#EFF6FF' : 'white',
                        borderBottom: '1px solid #E5E7EB',
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={(e) => {
                        if (!showAssessment) {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!showAssessment) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        backgroundColor: showAssessment ? '#DBEAFE' : '#F3F4F6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={showAssessment ? '#3B82F6' : '#6B7280'} strokeWidth="2">
                          <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="9" y="3" width="6" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 12h6" strokeLinecap="round"/>
                          <path d="M9 16h6" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.9rem', marginBottom: '0.125rem' }}>
                          T1D Readiness Assessment
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                          Evaluate Type 1 Diabetes care readiness
                        </div>
                      </div>
                      {showAssessment && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" style={{ marginLeft: 'auto' }}>
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </a>

                    {/* T1D Readiness Incidence - HealthDataVisualizer2 */}
                    <a
                      href="#incidence"
                      onClick={(e) => {
                        e.preventDefault();
                        navigateTo('incidence');
                        setShowPolicyDropdown(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '1rem 1.25rem',
                        textDecoration: 'none',
                        color: showIncidence ? '#3B82F6' : '#1F2937',
                        backgroundColor: showIncidence ? '#EFF6FF' : 'white',
                        borderBottom: '1px solid #E5E7EB',
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={(e) => {
                        if (!showIncidence) {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!showIncidence) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        backgroundColor: showIncidence ? '#DBEAFE' : '#F3F4F6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={showIncidence ? '#3B82F6' : '#6B7280'} strokeWidth="2">
                          <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18 9l-5 5-4-4-3 3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.9rem', marginBottom: '0.125rem' }}>
                          T1D Readiness Incidence
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                          Type 1 Diabetes incidence data explorer
                        </div>
                      </div>
                      {showIncidence && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" style={{ marginLeft: 'auto' }}>
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </a>

                    {/* Fiscal Space - HealthDataVisualizer */}
                    <a
                      href="#fiscal-space"
                      onClick={(e) => {
                        e.preventDefault();
                        navigateTo('fiscal-space');
                        setShowPolicyDropdown(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '1rem 1.25rem',
                        textDecoration: 'none',
                        color: showFiscalSpace ? '#3B82F6' : '#1F2937',
                        backgroundColor: showFiscalSpace ? '#EFF6FF' : 'white',
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={(e) => {
                        if (!showFiscalSpace) {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!showFiscalSpace) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        backgroundColor: showFiscalSpace ? '#DBEAFE' : '#F3F4F6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={showFiscalSpace ? '#3B82F6' : '#6B7280'} strokeWidth="2">
                          <line x1="12" y1="1" x2="12" y2="23" strokeLinecap="round"/>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.9rem', marginBottom: '0.125rem' }}>
                          Fiscal Space
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                          Financial capacity analysis for T1D programs
                        </div>
                      </div>
                      {showFiscalSpace && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" style={{ marginLeft: 'auto' }}>
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* CDiC Upload Portal */}
            <a 
              href="#upload-portal" 
              onClick={(e) => {
                e.preventDefault();
                navigateTo('upload-portal');
                setShowPolicyDropdown(false);
              }}
              style={{ 
                color: showUploadPortal ? '#3B82F6' : '#1F2937',
                textDecoration: 'none', 
                fontSize: '0.95rem', 
                fontWeight: '500',
                padding: '0.5rem 0',
                borderBottom: showUploadPortal ? '2px solid #3B82F6' : '2px solid transparent'
              }}
            >
              CDiC Upload Portal
            </a>

            {/* About Us */}
            <a 
              href="https://hsph.harvard.edu/research/health-systems-innovation-lab/resources/health-systems-visualizer/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: '#1F2937',
                textDecoration: 'none', 
                fontSize: '0.95rem', 
                fontWeight: '500',
                padding: '0.5rem 0',
                borderBottom: '2px solid transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#3B82F6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#1F2937';
              }}
            >
              About Us
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          </nav>
        </div>
      </div>

      {/* ─── MAIN CONTENT ─── */}
      <div style={{ flex: 1, padding: showFramework ? '0' : '2rem' }}>
        {showAssessment ? (
          <Assessment />
        ) : showIncidence ? (
          <HealthDataVisualizer2 />
        ) : showFiscalSpace ? (
          <HealthDataVisualizer />
        ) : showOECDExplorer ? (
          <OECDExplorer 
            skipLanding={true} 
            initialQuery={searchQuery}
            initialVariableSelection={initialVariableSelection}
          />
        ) : showFramework ? (
          <FrameworkExplorer />
        ) : showUploadPortal ? (
          /* ─── CDiC Upload Portal placeholder ─── */
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center',
            padding: '4rem 2rem'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              padding: '3rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '12px',
                backgroundColor: '#EFF6FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem auto'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 0.75rem 0'
              }}>
                CDiC Upload Portal
              </h2>
              <p style={{
                fontSize: '1.05rem',
                color: '#6B7280',
                margin: '0 0 2rem 0',
                lineHeight: '1.6'
              }}>
                This portal is currently under development. Soon you will be able to upload and manage CDiC study data directly from here.
              </p>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: '8px',
                padding: '0.75rem 1.25rem',
                fontSize: '0.875rem',
                color: '#92400E'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Coming Soon
              </div>
            </div>
          </div>
        ) : (
          /* ─── HOME / LANDING ─── */
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem'
          }}>
            {/* Hero / Search */}
            <div style={{
              backgroundColor: 'white',
              padding: '3rem 2rem',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                gap: '3rem',
                alignItems: 'center',
                marginBottom: '2rem'
              }}>
                <div style={{ flex: 1 }}>
                  <h1 style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: '#1F2937',
                    marginBottom: '1rem',
                    margin: 0
                  }}>
                    Health Systems Visualizer for Type 1 Diabetes
                  </h1>
                  <p style={{
                    fontSize: '1.125rem',
                    color: '#6B7280',
                    margin: '1rem 0 0 0',
                    lineHeight: '1.6'
                  }}>
                  </p>
                </div>

                <div style={{ flex: '0 0 500px', minWidth: '500px' }}>
                  <form onSubmit={handleSearchSubmit} style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '0.5rem',
                    position: 'relative'
                  }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={() => {
                          if (autocompleteOptions.length > 0) {
                            setShowAutocomplete(true);
                          }
                        }}
                        placeholder="Search health indicators..."
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          fontSize: '1rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px 0 0 8px',
                          outline: 'none'
                        }}
                        onFocusCapture={(e) => e.target.style.borderColor = '#3B82F6'}
                        onBlur={(e) => {
                          setTimeout(() => {
                            e.target.style.borderColor = '#e5e7eb';
                          }, 200);
                        }}
                      />
                      
                      {showAutocomplete && autocompleteOptions.length > 0 && (
                        <div
                          ref={autocompleteRef}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '2px solid #3B82F6',
                            borderTop: 'none',
                            borderRadius: '0 0 0 8px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                        >
                          {autocompleteOptions.map((option, index) => (
                            <div
                              key={index}
                              onClick={() => handleSelectOption(option)}
                              style={{
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                backgroundColor: index === selectedIndex ? '#EFF6FF' : 'white',
                                borderBottom: index < autocompleteOptions.length - 1 ? '1px solid #e5e7eb' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                              onMouseEnter={(e) => {
                                if (index !== selectedIndex) {
                                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (index !== selectedIndex) {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              <span style={{ fontSize: '0.95rem', color: '#1F2937' }}>
                                {option.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <button
                      type="submit"
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0 8px 8px 0',
                        fontSize: '1rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#2563EB'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#3B82F6'}
                    >
                      Search
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Two cards */}
            <div style={{ display: 'flex', gap: '2rem', width: '100%' }}>
              {/* Card 1 — World Map */}
              <div 
                onClick={() => navigateTo('observatory')}
                onMouseEnter={() => setCard1Hovered(true)}
                onMouseLeave={() => setCard1Hovered(false)}
                style={{
                  flex: 1,
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: card1Hovered ? 'translateY(-4px)' : 'translateY(0)',
                  boxShadow: card1Hovered ? '0 12px 24px rgba(0,0,0,0.12)' : '0 2px 4px rgba(0,0,0,0.04)'
                }}
              >
                <div style={{
                  padding: '1.5rem',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: '0 0 0.5rem 0'
                  }}>
                    Health Systems Visualizer
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    margin: 0
                  }}>
                  </p>
                </div>

                <div style={{ padding: '1rem' }}>
                  <AnimatedWorldMap />
                </div>

                <div style={{
                  padding: '1rem 1.5rem',
                  backgroundColor: card1Hovered ? '#3B82F6' : '#F9FAFB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease'
                }}>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: card1Hovered ? 'white' : '#3B82F6'
                  }}>
                    Explore Health Data
                  </span>
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke={card1Hovered ? 'white' : '#3B82F6'} 
                    strokeWidth="2"
                    style={{
                      transform: card1Hovered ? 'translateX(4px)' : 'translateX(0)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              </div>

              {/* Card 2 — Fiscal Space Line Chart */}
              <div 
                onClick={() => navigateTo('assessment')}
                onMouseEnter={() => setCard2Hovered(true)}
                onMouseLeave={() => setCard2Hovered(false)}
                style={{
                  flex: 1,
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: card2Hovered ? 'translateY(-4px)' : 'translateY(0)',
                  boxShadow: card2Hovered ? '0 12px 24px rgba(0,0,0,0.12)' : '0 2px 4px rgba(0,0,0,0.04)'
                }}
              >
                <div style={{
                  padding: '1.5rem',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: '0 0 0.5rem 0'
                  }}>
                    GC-CDiC Studies
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    margin: 0
                  }}>
                  </p>
                </div>

                <div style={{ padding: '1rem' }}>
                  <AnimatedLineChart />
                </div>

                <div style={{
                  padding: '1rem 1.5rem',
                  backgroundColor: card2Hovered ? '#F59E0B' : '#F9FAFB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease'
                }}>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: card2Hovered ? 'white' : '#F59E0B'
                  }}>
                    Explore Studies
                  </span>
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke={card2Hovered ? 'white' : '#F59E0B'} 
                    strokeWidth="2"
                    style={{
                      transform: card2Hovered ? 'translateX(4px)' : 'translateX(0)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── FOOTER ─── */}
      {!showFramework && (
        <div style={{
          backgroundColor: 'white',
          borderTop: '1px solid #e5e7eb',
          padding: '1.5rem 2rem',
          marginTop: 'auto'
        }}>
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <p style={{
              fontSize: '0.875rem',
              color: '#6B7280',
              margin: 0
            }}>
              © Health Systems Innovation Lab, Harvard University, 2025
            </p>
          </div>
        </div>
      )}

      {/* ─── METADATA MODAL ─── */}
      {showMetadata && selectedVariableMetadata && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem'
          }}
          onClick={() => {
            setShowMetadata(false);
            setSelectedVariableMetadata(null);
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '800px',
              maxHeight: '80vh',
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1F2937'
              }}>
                Variable Metadata
              </h3>
              <button
                onClick={() => {
                  setShowMetadata(false);
                  setSelectedVariableMetadata(null);
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{
              flex: 1,
              overflow: 'auto',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              padding: '1rem',
              backgroundColor: '#F9FAFB'
            }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {[
                  { key: 'Source', label: 'Source' },
                  { key: 'Component', label: 'Component' },
                  { key: 'Sub-component', label: 'Sub-component' },
                  { key: 'Variable', label: 'Variable' },
                  { key: 'Variable Definition', label: 'Variable Definition' },
                  { key: 'Measure', label: 'Unit of Measure' },
                  { key: 'Unit Standardization', label: 'Unit Standardization' },
                  { key: 'Date Range', label: 'Date Range' },
                  { key: 'URL', label: 'URL' }
                ].map(({ key, label }) => (
                  <div key={key} style={{ borderBottom: '1px solid #E5E7EB', paddingBottom: '0.75rem' }}>
                    <dt style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                      {label}:
                    </dt>
                    <dd style={{ fontSize: '0.875rem', color: '#1F2937', margin: 0, wordBreak: 'break-word' }}>
                      {key === 'URL' && selectedVariableMetadata[key] ? (
                        <a 
                          href={selectedVariableMetadata[key]} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ color: '#3B82F6', textDecoration: 'underline' }}
                        >
                          {selectedVariableMetadata[key]}
                        </a>
                      ) : (
                        selectedVariableMetadata[key] || 'Not specified'
                      )}
                    </dd>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
              <button
                onClick={() => {
                  setShowMetadata(false);
                  setSelectedVariableMetadata(null);
                }}
                style={{
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;